import React from 'react';
import {ApolloClient, NormalizedCacheObject, gql} from '@apollo/client';
import {Link, useParams} from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {useGetAuthors} from '../../api/graphql_author_list';
import {AuthorAggregateScore} from '../../types/generated/graphql';
import {useGetPendingCorrectiveActions} from '../../api/graphql_corrective_actions';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

const GET_AUTHOR_AGGREGATE_SCORES = gql`
  query getAuthorAggregateScores($authorIds: [String!]!, $profileId: ID) {
    getAuthorAggregateScores(authorIds: $authorIds, profileId: $profileId) {
      authorId
      score
      rating
      videoCount
      restrictedVideoCount
    }
  }
`;

type Tone = 'good' | 'attention' | 'warning';

function toneFromScore(score: number | null | undefined): Tone {
  if (score == null || !Number.isFinite(score)) return 'attention';
  if (score >= 7) return 'good';
  if (score >= 4) return 'attention';
  return 'warning';
}

// Score chip — tinted by tone so the verdict is legible at a glance
// without making the card itself loud. Border is the saturated edge
// of the tone; bg is the desaturated wash. Same shape as the rating
// chip below so the pair reads as one stacked unit.
const SCORE_PALETTE: Record<Tone, {bg: string; fg: string; border: string}> = {
  good: {bg: '#ECFDF5', fg: '#047857', border: '#A7F3D0'},
  attention: {bg: '#FEF3C7', fg: '#92400E', border: '#FCD34D'},
  warning: {bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5'},
};

// Rating chip — neutral grey by default; goes red when the channel
// crossed the restricted threshold. Beneficial doesn't override the
// grey because the score chip above already carries the green tone
// — a second green pill is redundant and made the card feel busy.
function ratingChipColors(
  rating: string | null | undefined,
  restricted: boolean,
): {bg: string; fg: string; border: string} {
  if (restricted || rating === 'HARMFUL') {
    return {bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5'};
  }
  return {bg: '#F1F5F9', fg: '#475569', border: '#E2E8F0'};
}

function ChannelsHeader({
  count,
  loading,
}: {
  count: number;
  loading: boolean;
}): React.JSX.Element {
  return (
    <Box sx={{py: 3}}>
      <Typography sx={{fontSize: 22, fontWeight: 800, color: '#0F172A', lineHeight: 1.2}}>
        Channels
      </Typography>
      <Typography sx={{fontSize: 13, color: '#64748B', mt: 0.5}}>
        {loading
          ? 'Loading channels…'
          : `${count.toLocaleString()} channel${count === 1 ? '' : 's'} that have shown up across this profile`}
      </Typography>
    </Box>
  );
}

function GridSkeleton(): React.JSX.Element {
  return (
    <Grid container spacing={{xs: 2, md: 3}} columns={{xs: 4, sm: 8, md: 12}}>
      {Array.from({length: 12}).map((_, i) => (
        <Grid key={i} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
          <Skeleton variant="rounded" height={260} />
        </Grid>
      ))}
    </Grid>
  );
}

interface Author {
  id: string;
  name: string;
  thumbnails: Array<{url: string}>;
}

function AuthorCard({
  pid,
  author,
  score,
  scoresLoading,
  pendingActionCount,
}: {
  pid: string;
  author: Author;
  score: AuthorAggregateScore | undefined;
  scoresLoading: boolean;
  pendingActionCount: number;
}): React.JSX.Element {
  const tone = toneFromScore(score?.score);
  const scorePalette = SCORE_PALETTE[tone];
  const hasFinite = score?.score != null && Number.isFinite(score.score);
  const hasRestricted =
    score?.restrictedVideoCount != null && score.restrictedVideoCount > 0;
  const ratingPalette = ratingChipColors(score?.rating, hasRestricted);

  return (
    <Box
      component={Link}
      to={`/profile/${pid}/channel/${author.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        height: '100%',
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        transition: 'border-color 120ms, transform 120ms, box-shadow 120ms',
        '&:hover': {
          borderColor: '#A5B4FC',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
        },
      }}
    >
      {/* Square avatar — channel thumbnails on YouTube are 1:1 by
          convention; cropping to 16:9 was making them feel like
          video stills, which the card isn't. Inset slightly so the
          rounded card border doesn't clip the avatar's own edge. */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          backgroundImage: author.thumbnails?.[0]?.url
            ? `url(${author.thumbnails[0].url})`
            : undefined,
          backgroundColor: '#F1F5F9',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderBottom: '1px solid #E2E8F0',
        }}
      >
        {pendingActionCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              px: 0.75,
              py: 0.25,
              borderRadius: 999,
              bgcolor: '#FEF3C7',
              color: '#92400E',
              border: '1px solid #FCD34D',
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >
            {pendingActionCount} pending
          </Box>
        )}
      </Box>
      <Box
        sx={{
          p: 1.75,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 800,
            color: '#0F172A',
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.5em',
          }}
        >
          {author.name}
        </Typography>

        {scoresLoading && !score ? (
          <Stack spacing={0.75}>
            <Skeleton variant="rounded" width={70} height={22} />
            <Skeleton variant="rounded" width={86} height={22} />
            <Skeleton variant="text" width="40%" height={14} />
          </Stack>
        ) : score ? (
          <Stack spacing={0.75} alignItems="flex-start" sx={{mt: 'auto'}}>
            {/* Score and rating chips stack vertically — they're two
                facts about the same channel, not adjacent peers. The
                vertical rhythm matches the title above and reads as
                one block. */}
            {hasFinite && (
              <Box
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: scorePalette.bg,
                  color: scorePalette.fg,
                  border: `1px solid ${scorePalette.border}`,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {score.score!.toFixed(1)}/10
              </Box>
            )}
            {score.rating && (
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: ratingPalette.bg,
                  color: ratingPalette.fg,
                  border: `1px solid ${ratingPalette.border}`,
                }}
              >
                {score.rating}
              </Box>
            )}
            <Stack direction="row" alignItems="center" spacing={1} sx={{pt: 0.25}}>
              <Typography sx={{fontSize: 12, color: '#64748B'}}>
                {score.videoCount} video{score.videoCount === 1 ? '' : 's'}
              </Typography>
              {hasRestricted && (
                <>
                  <Box sx={{width: 3, height: 3, borderRadius: '50%', bgcolor: '#CBD5E1'}} />
                  <Typography sx={{fontSize: 12, color: '#991B1B', fontWeight: 600}}>
                    {score.restrictedVideoCount} restricted
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
        ) : (
          // No score yet — channel cache is cold or no scored videos.
          // Show the fact rather than nothing so the card doesn't
          // collapse weirdly relative to its scored siblings.
          <Typography sx={{fontSize: 12, color: '#94A3B8', fontStyle: 'italic', mt: 'auto'}}>
            No score yet
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function ListAuthors({client}: Props): React.JSX.Element {
  const {pid} = useParams();
  const {data, loading, error} = useGetAuthors(client, pid as string);
  const {data: pendingActionsData} = useGetPendingCorrectiveActions(client, pid as string);
  const pendingByChannel = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const a of pendingActionsData?.getPendingCorrectiveActions ?? []) {
      const cid =
        (a as {kind?: string; channel?: {id?: string}; video?: {author?: {id?: string}}}).kind === 'CHANNEL'
          ? (a as {channel?: {id?: string}}).channel?.id
          : (a as {video?: {author?: {id?: string}}}).video?.author?.id;
      if (cid) map.set(cid, (map.get(cid) ?? 0) + 1);
    }
    return map;
  }, [pendingActionsData]);
  const [authorScores, setAuthorScores] = React.useState<Map<string, AuthorAggregateScore>>(
    new Map(),
  );
  const [scoresLoading, setScoresLoading] = React.useState(false);

  React.useEffect(() => {
    if (data?.getAuthors && data.getAuthors.length > 0) {
      const authorIds = data.getAuthors.map(a => a.id);
      setScoresLoading(true);
      client
        .query({
          query: GET_AUTHOR_AGGREGATE_SCORES,
          variables: {authorIds, profileId: pid},
          fetchPolicy: 'network-only',
        })
        .then(result => {
          const map = new Map<string, AuthorAggregateScore>();
          result.data?.getAuthorAggregateScores?.forEach((s: AuthorAggregateScore) => {
            map.set(s.authorId, s);
          });
          setAuthorScores(map);
        })
        .catch(err => {
          console.error('Error loading author scores:', err);
        })
        .finally(() => {
          setScoresLoading(false);
        });
    }
  }, [data, client, pid]);

  const authors = data?.getAuthors || [];

  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#F8FAFC'}}>
      <Container maxWidth="md" sx={{py: 0}}>
        <ChannelsHeader count={authors.length} loading={loading && !data} />

        {error && (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid #FECACA',
              bgcolor: '#FEF2F2',
              p: 3,
              mb: 3,
            }}
          >
            <Typography sx={{fontSize: 13, color: '#991B1B'}}>
              Couldn&rsquo;t load channels: {error.message}
            </Typography>
          </Box>
        )}

        {loading && !data && (
          <Box sx={{pb: 6}}>
            <GridSkeleton />
          </Box>
        )}

        {data && authors.length === 0 && (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              p: 4,
              textAlign: 'center',
              mb: 6,
            }}
          >
            <Typography sx={{fontSize: 14, color: '#64748B'}}>
              No channels yet. They&rsquo;ll show up here as videos are watched or recommended.
            </Typography>
          </Box>
        )}

        {data && pid && authors.length > 0 && (
          <Box sx={{pb: 6}}>
            <Grid container spacing={{xs: 2, md: 3}} columns={{xs: 4, sm: 8, md: 12}}>
              {authors.map(author => (
                <Grid key={author.id} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
                  <AuthorCard
                    pid={pid}
                    author={author}
                    score={authorScores.get(author.id)}
                    scoresLoading={scoresLoading}
                    pendingActionCount={pendingByChannel.get(author.id) ?? 0}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}
